import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';

import { FetchEmployees } from '../../employees/employees.actions';
import { EmployeeState } from '../../employees/employees.state';
import { Employee } from '../../models/Employee';
import { FleetImportResponse, FleetVehicle, FleetVehicleDetail, FleetVehicleHistory } from '../../models/Fleet';
import { UploadedDocument } from '../../models/UploadedDocument';
import { AddFileToObjectModalComponent } from '../../shared/global-files/add-file-to-object-modal/add-file-to-object-modal.component';
import { GlobalFilesService } from '../../shared/global-files/global-files.service';
import { FleetService } from '../fleet.service';
import { openExternalUrl } from '../../shared/safe-url';

@Component({
    selector: 'app-fleet-overview',
    templateUrl: './fleet-overview.component.html',
    styleUrls: ['./fleet-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class FleetOverviewComponent implements OnInit {
    @ViewChild('vehicleDetailModal') vehicleDetailModal: TemplateRef<any>;
    @ViewChild('documentExtractModal') documentExtractModal: TemplateRef<any>;

    vehicles: FleetVehicle[] = [];
    filteredVehicles: FleetVehicle[] = [];
    selectedVehicle: FleetVehicleDetail;

    filter = '';
    showActiveOnly = true;
    loadingVehicles = false;
    loadingDetail = false;
    uploading = false;
    saving = false;

    selectedUploadFile: File;
    importResult: FleetImportResponse;

    notes = '';
    selectedDriverUuid = '';
    activeTab = 'information';
    detailModalRef: NgbModalRef;
    documentExtractModalRef: NgbModalRef;
    selectedExtractionRow: FleetDocumentRow | null = null;

    fleetDocumentRows: FleetDocumentRow[] = [
        { type: 'INSURANCE', label: 'Verzekering', extractedData: null, extractionStatus: null },
        { type: 'REGISTRATION_CERTIFICATE', label: 'Inschrijvingsbewijs', extractedData: null, extractionStatus: null },
        { type: 'CONFORMITY_CERTIFICATE', label: 'Gelijkvormigheidsattest', extractedData: null, extractionStatus: null },
    ];
    loadingFleetDocuments = false;

    overviewSortField: keyof FleetVehicle = 'licensePlate';
    overviewSortDirection: 'asc' | 'desc' = 'asc';
    historySortField: keyof FleetVehicleHistory = 'usageStartDate';
    historySortDirection: 'asc' | 'desc' = 'desc';

    @Select(EmployeeState.Employees) employees$: Observable<Employee[]>;
    employees: Employee[] = [];

    constructor(
        private fleetService: FleetService,
        private globalFilesService: GlobalFilesService,
        private titleService: Title,
        private logger: NGXLogger,
        private toastr: ToastrService,
        private modalService: NgbModal,
        private store: Store,
    ) {
        this.titleService.setTitle('Fleet');
    }

    ngOnInit(): void {
        this.refreshVehicles();
        this.employees$.subscribe((employees) => {
            if (employees === null) {
                this.store.dispatch(new FetchEmployees());
                return;
            }
            this.employees = employees;
        });
    }

    get activeVehiclesCount(): number {
        return (this.vehicles || []).filter((vehicle) => this.isVehicleCurrentlyActive(vehicle)).length;
    }

    refreshVehicles(): void {
        this.loadingVehicles = true;
        this.fleetService.getVehicles().subscribe({
            next: (vehicles) => {
                this.vehicles = vehicles || [];
                this.applyFilter();
                this.ensureSelection();
                this.loadingVehicles = false;
            },
            error: (error) => {
                this.loadingVehicles = false;
                this.logger.error('Failed to fetch fleet vehicles', error);
                this.toastr.error(error?.error?.message || 'Failed to load fleet vehicles');
            },
        });
    }

    onFileSelected(event: Event): void {
        const target = event.target as HTMLInputElement;
        const file = target?.files && target.files.length > 0 ? target.files[0] : null;
        this.selectedUploadFile = file || null;
    }

    uploadCsv(): void {
        if (!this.selectedUploadFile) {
            this.toastr.warning('Selecteer eerst een CSV bestand');
            return;
        }

        this.uploading = true;
        this.fleetService.upload(this.selectedUploadFile).subscribe({
            next: (result) => {
                this.importResult = result;
                this.uploading = false;
                this.toastr.success('Fleet CSV succesvol geïmporteerd');
                this.resetSelectedVehicleState(true);
                this.refreshVehicles();
            },
            error: (error) => {
                this.uploading = false;
                this.logger.error('Failed to upload fleet csv', error);
                this.toastr.error(error?.error?.message || 'Upload mislukt');
            },
        });
    }

    applyFilter(): void {
        const value = (this.filter || '').trim().toLowerCase();
        this.filteredVehicles = this.vehicles.filter((vehicle) => {
            const matchesSearch =
                !value ||
                vehicle.licensePlate?.toLowerCase().includes(value) ||
                vehicle.vehicleModel?.toLowerCase().includes(value) ||
                vehicle.assignedEmployeeName?.toLowerCase().includes(value);

            const matchesActiveToggle = !this.showActiveOnly || this.isVehicleCurrentlyActive(vehicle);

            return matchesSearch && matchesActiveToggle;
        });

        this.sortOverviewData();
    }

    onFilterChanged(): void {
        this.applyFilter();
        this.ensureSelection();
    }

    private isVehicleCurrentlyActive(vehicle: FleetVehicle): boolean {
        const today = new Date();
        const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        const fleetStartDate = vehicle.fleetStartDate ? new Date(vehicle.fleetStartDate) : null;
        const fleetEndDate = vehicle.fleetEndDate ? new Date(vehicle.fleetEndDate) : null;

        const fleetStarted = !fleetStartDate || fleetStartDate <= todayDateOnly;
        const fleetNotEnded = !fleetEndDate || fleetEndDate >= todayDateOnly;

        return fleetStarted && fleetNotEnded;
    }

    private ensureSelection(): void {
        if (!this.filteredVehicles?.length) {
            this.selectedVehicle = null;
            return;
        }

        const selectedStillVisible = this.selectedVehicle?.uuid ? this.filteredVehicles.some((vehicle) => vehicle.uuid === this.selectedVehicle.uuid) : false;

        if (!selectedStillVisible) {
            this.selectVehicleById(this.filteredVehicles[0].uuid);
        }
    }

    requestingDocumentsFor: string | null = null;

    missingDocumentsTitle(vehicle: FleetVehicle): string {
        const missing = (vehicle.missingDocuments || []).join(', ');
        const driver = vehicle.assignedEmployeeName ? ` Klik om ${vehicle.assignedEmployeeName} te mailen.` : ' Er is geen bestuurder gekoppeld.';
        return `Ontbreekt: ${missing || 'onbekend'}.${driver}`;
    }

    /** Mails the current driver a link to upload what is missing; asks first, because it is a real mail. */
    requestDocuments(vehicle: FleetVehicle): void {
        if (!vehicle.assignedEmployeeName) {
            this.toastr.warning(`Er is geen bestuurder gekoppeld aan ${vehicle.licensePlate}.`);
            return;
        }
        const again = vehicle.documentsRequestedAt ? ' Er werd al eerder een mail gestuurd; dit stuurt een nieuwe.' : '';
        const missing = (vehicle.missingDocuments || []).join(', ');
        if (!window.confirm(`${vehicle.assignedEmployeeName} een mail sturen om ${missing} op te laden voor ${vehicle.licensePlate}?${again}`)) {
            return;
        }
        this.requestingDocumentsFor = vehicle.uuid;
        this.fleetService.requestDocuments(vehicle.uuid).subscribe({
            next: (result) => {
                this.requestingDocumentsFor = null;
                vehicle.documentsRequestedAt = result.requestedAt;
                this.toastr.success(`Mail verstuurd naar ${result.driverName} (${result.email}).`);
            },
            error: (err) => {
                this.requestingDocumentsFor = null;
                this.toastr.error(err?.error?.message || 'De mail kon niet verstuurd worden.');
            },
        });
    }

    selectVehicle(vehicle: FleetVehicle): void {
        this.selectVehicleById(vehicle.uuid, true);
    }

    selectVehicleById(vehicleUuid: string, openModal = false): void {
        this.loadingDetail = true;
        this.fleetService.getVehicle(vehicleUuid).subscribe({
            next: (vehicle) => {
                this.selectedVehicle = vehicle;
                this.notes = vehicle.notes || '';
                this.selectedDriverUuid = vehicle.assignedEmployeeUuid || '';
                this.activeTab = 'information';
                this.loadingDetail = false;
                this.loadFleetDocuments();

                if (openModal) {
                    this.openVehicleDetailModal();
                }
            },
            error: (error) => {
                this.loadingDetail = false;
                if (error?.status === 404) {
                    this.resetSelectedVehicleState(false);
                    this.refreshVehicles();
                    return;
                }
                this.logger.error('Failed to fetch fleet vehicle detail', error);
                this.toastr.error(error?.error?.message || 'Kon detail niet laden');
            },
        });
    }

    private resetSelectedVehicleState(closeModal: boolean): void {
        this.selectedVehicle = null;
        this.notes = '';
        this.selectedDriverUuid = '';
        this.fleetDocumentRows = this.fleetDocumentRows.map((row) => ({
            ...row,
            documentId: null,
            url: null,
            extractedData: null,
            extractionStatus: null,
            extracting: false,
        }));

        if (closeModal && this.detailModalRef) {
            this.detailModalRef.close();
            this.detailModalRef = null;
        }
    }

    private openVehicleDetailModal(): void {
        if (this.detailModalRef) {
            this.detailModalRef.close();
        }

        this.detailModalRef = this.modalService.open(this.vehicleDetailModal, {
            windowClass: 'modal-prompt modal-prompt--wide',
            size: 'xl',
            animation: false,
        });
    }

    toggleOverviewSort(field: keyof FleetVehicle): void {
        if (this.overviewSortField === field) {
            this.overviewSortDirection = this.overviewSortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.overviewSortField = field;
            this.overviewSortDirection = 'asc';
        }

        this.sortOverviewData();
        this.ensureSelection();
    }

    toggleHistorySort(field: keyof FleetVehicleHistory): void {
        if (this.historySortField === field) {
            this.historySortDirection = this.historySortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.historySortField = field;
            this.historySortDirection = 'asc';
        }
    }

    getOverviewSortIndicator(field: keyof FleetVehicle): string {
        if (this.overviewSortField !== field) {
            return '';
        }
        return this.overviewSortDirection === 'asc' ? '▲' : '▼';
    }

    getHistorySortIndicator(field: keyof FleetVehicleHistory): string {
        if (this.historySortField !== field) {
            return '';
        }
        return this.historySortDirection === 'asc' ? '▲' : '▼';
    }

    getSortedHistory(): FleetVehicleHistory[] {
        const items = [...(this.selectedVehicle?.history || [])];
        const direction = this.historySortDirection === 'asc' ? 1 : -1;
        const field = this.historySortField;

        items.sort((left, right) => this.compareValues(left[field], right[field]) * direction);
        return items;
    }

    private sortOverviewData(): void {
        const direction = this.overviewSortDirection === 'asc' ? 1 : -1;
        const field = this.overviewSortField;

        this.filteredVehicles = [...this.filteredVehicles].sort((left, right) => this.compareValues(left[field], right[field]) * direction);
    }

    private compareValues(left: unknown, right: unknown): number {
        const leftNull = left === null || left === undefined || left === '';
        const rightNull = right === null || right === undefined || right === '';

        if (leftNull && rightNull) {
            return 0;
        }
        if (leftNull) {
            return 1;
        }
        if (rightNull) {
            return -1;
        }

        const leftDate = this.tryParseDate(left);
        const rightDate = this.tryParseDate(right);
        if (leftDate !== null && rightDate !== null) {
            return leftDate - rightDate;
        }

        if (typeof left === 'number' && typeof right === 'number') {
            return left - right;
        }

        return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
    }

    private tryParseDate(value: unknown): number | null {
        if (value instanceof Date) {
            return value.getTime();
        }

        if (typeof value === 'string') {
            const parsed = Date.parse(value);
            return Number.isNaN(parsed) ? null : parsed;
        }

        return null;
    }

    saveVehicleMeta(): void {
        if (!this.selectedVehicle?.uuid) return;

        this.saving = true;
        this.fleetService
            .updateVehicle(this.selectedVehicle.uuid, {
                notes: this.notes,
                assignedEmployeeUuid: this.selectedDriverUuid || undefined,
            })
            .subscribe({
                next: (updatedVehicle) => {
                    this.selectedVehicle = updatedVehicle;
                    this.selectedDriverUuid = updatedVehicle.assignedEmployeeUuid || '';
                    this.saving = false;
                    this.toastr.success('Fleet gegevens opgeslagen');
                    this.refreshVehicles();
                },
                error: (error) => {
                    this.saving = false;
                    this.logger.error('Failed to save fleet metadata', error);
                    this.toastr.error(error?.error?.message || 'Opslaan mislukt');
                },
            });
    }

    openStandardDocumentUpload(documentType: FleetDocumentType): void {
        if (!this.selectedVehicle?.uuid) {
            return;
        }

        const modalRef = this.modalService.open(AddFileToObjectModalComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });
        modalRef.componentInstance.objectId = this.selectedVehicle.uuid;
        modalRef.componentInstance.presetDocumentType = documentType;
        modalRef.componentInstance.presetDescription = this.getDocumentLabel(documentType);

        modalRef.result
            .then(() => {
                this.loadFleetDocuments();
            })
            .catch(() => {
                this.loadFleetDocuments();
            });
    }

    extractStandardDocument(documentType: FleetDocumentType): void {
        const row = this.fleetDocumentRows.find((item) => item.type === documentType);
        if (!row?.documentId) {
            this.toastr.info('Upload eerst het document.');
            return;
        }

        row.extracting = true;
        this.globalFilesService.extractFleetDocument(row.documentId).subscribe({
            next: (updatedDocument) => {
                row.extracting = false;
                row.extractedData = updatedDocument.extractedData || null;
                row.extractionStatus = updatedDocument.extractionStatus || null;
                row.extractedLicensePlate = this.firstNonEmpty(
                    updatedDocument.extractedLicensePlate,
                    this.getExtractedValue(row.extractedData, ['licensePlate', 'license_plate', 'plaat', 'numberPlate']),
                );
                row.extractedVin = this.firstNonEmpty(
                    updatedDocument.extractedVin,
                    this.getExtractedValue(row.extractedData, ['vin', 'VIN', 'chassisNumber', 'chassisNo', 'chassis', 'fieldE', 'e']),
                );
                row.extractedPolicyNumber = this.firstNonEmpty(
                    updatedDocument.extractedPolicyNumber,
                    this.getExtractedValue(row.extractedData, ['policyNumber', 'policy_number', 'polisnummer', 'polisNummer']),
                );
                row.extractedValidFrom = this.firstNonEmpty(
                    updatedDocument.extractedValidFrom,
                    this.getExtractedValue(row.extractedData, ['validFrom', 'valid_from', 'startDate']),
                );
                row.extractedValidTo = this.firstNonEmpty(
                    updatedDocument.extractedValidTo,
                    this.getExtractedValue(row.extractedData, ['validTo', 'valid_to', 'endDate']),
                );
                row.extractedFirstRegistrationDate = this.firstNonEmpty(
                    updatedDocument.extractedFirstRegistrationDate,
                    this.getExtractedValue(row.extractedData, ['firstRegistrationDate', 'first_registration_date']),
                );
                this.toastr.success(`Extractie voltooid voor ${row.label}.`);
            },
            error: (error) => {
                row.extracting = false;
                this.toastr.error(error?.error?.message || `Extractie mislukt voor ${row.label}.`);
            },
        });
    }

    openStandardDocument(documentType: FleetDocumentType): void {
        const row = this.fleetDocumentRows.find((item) => item.type === documentType);
        if (!row?.url) {
            this.toastr.info('Geen document beschikbaar om te openen.');
            return;
        }

        openExternalUrl(row.url);
    }

    openDocumentExtractionPopup(documentType: FleetDocumentType): void {
        const row = this.fleetDocumentRows.find((item) => item.type === documentType);
        if (!row) {
            return;
        }

        this.selectedExtractionRow = row;

        if (this.documentExtractModalRef) {
            this.documentExtractModalRef.close();
        }

        this.documentExtractModalRef = this.modalService.open(this.documentExtractModal, {
            windowClass: 'modal-prompt',
            animation: false,
        });

        this.documentExtractModalRef.result
            .then(() => {
                this.selectedExtractionRow = null;
            })
            .catch(() => {
                this.selectedExtractionRow = null;
            });
    }

    getDocumentRow(documentType: FleetDocumentType): FleetDocumentRow | undefined {
        return this.fleetDocumentRows.find((item) => item.type === documentType);
    }

    getStructuredExtractedEntries(row: FleetDocumentRow, includeEmpty = false): Array<{ key: string; value: string }> {
        if (!row?.documentId) {
            return [];
        }

        const entries: Array<{ key: string; value: string }> = [];

        const pushIfValue = (key: string, value: string | null) => {
            if (value) {
                entries.push({ key, value });
            } else if (includeEmpty) {
                entries.push({ key, value: '-' });
            }
        };

        if (row.type === 'INSURANCE') {
            pushIfValue(
                'Nummerplaat',
                this.firstNonEmpty(
                    row.extractedLicensePlate,
                    this.getExtractedValue(row.extractedData, ['licensePlate', 'license_plate', 'plaat', 'numberPlate']),
                ),
            );
            pushIfValue(
                'VIN',
                this.firstNonEmpty(
                    row.extractedVin,
                    this.getExtractedValue(row.extractedData, ['vin', 'VIN', 'chassisNumber', 'chassisNo', 'chassis', 'fieldE', 'e']),
                ),
            );
            pushIfValue(
                'Polisnummer',
                this.firstNonEmpty(
                    row.extractedPolicyNumber,
                    this.getExtractedValue(row.extractedData, ['policyNumber', 'policy_number', 'polisnummer', 'polisNummer']),
                ),
            );
            pushIfValue(
                'Geldig van',
                this.firstNonEmpty(row.extractedValidFrom, this.getExtractedValue(row.extractedData, ['validFrom', 'valid_from', 'startDate'])),
            );
            pushIfValue('Geldig tot', this.firstNonEmpty(row.extractedValidTo, this.getExtractedValue(row.extractedData, ['validTo', 'valid_to', 'endDate'])));
        }

        if (row.type === 'REGISTRATION_CERTIFICATE') {
            pushIfValue(
                'Nummerplaat',
                this.firstNonEmpty(
                    row.extractedLicensePlate,
                    this.getExtractedValue(row.extractedData, ['licensePlate', 'license_plate', 'plaat', 'numberPlate']),
                ),
            );
            pushIfValue(
                'VIN',
                this.firstNonEmpty(
                    row.extractedVin,
                    this.getExtractedValue(row.extractedData, ['vin', 'VIN', 'chassisNumber', 'chassisNo', 'chassis', 'fieldE', 'e']),
                ),
            );
            pushIfValue('Houder', this.getExtractedValue(row.extractedData, ['holderName', 'houder', 'holder']));
            pushIfValue('Merk', this.getExtractedValue(row.extractedData, ['make', 'merk']));
            pushIfValue('Model', this.getExtractedValue(row.extractedData, ['model']));
            pushIfValue(
                'Eerste inschrijving',
                this.firstNonEmpty(
                    row.extractedFirstRegistrationDate,
                    this.getExtractedValue(row.extractedData, ['firstRegistrationDate', 'first_registration_date']),
                ),
            );
        }

        if (row.type === 'CONFORMITY_CERTIFICATE') {
            pushIfValue(
                'VIN',
                this.firstNonEmpty(
                    row.extractedVin,
                    this.getExtractedValue(row.extractedData, ['vin', 'VIN', 'chassisNumber', 'chassisNo', 'chassis', 'fieldE', 'e']),
                ),
            );
            pushIfValue('Merk', this.getExtractedValue(row.extractedData, ['make', 'merk']));
            pushIfValue('Model', this.getExtractedValue(row.extractedData, ['model']));
            pushIfValue('Variant', this.getExtractedValue(row.extractedData, ['variant']));
            pushIfValue('EU typegoedkeuring', this.getExtractedValue(row.extractedData, ['euTypeApprovalNumber', 'eu_type_approval_number']));
            pushIfValue('Brandstof', this.getExtractedValue(row.extractedData, ['fuelType', 'fuel_type']));
            pushIfValue('CO2', this.getExtractedValue(row.extractedData, ['co2Emission', 'co2_emission']));
        }

        if (entries.length > 0) {
            return entries;
        }

        return this.getExtractedEntries(row);
    }

    getExtractedEntries(row: FleetDocumentRow): Array<{ key: string; value: string }> {
        if (!row?.extractedData) {
            return [];
        }

        return Object.keys(row.extractedData)
            .filter((key) => key !== 'documentType')
            .map((key) => ({
                key,
                value: row.extractedData[key] === null || row.extractedData[key] === undefined ? '-' : String(row.extractedData[key]),
            }))
            .filter((entry) => entry.value !== '');
    }

    getInsuranceExpiryWarning(row: FleetDocumentRow): string | null {
        if (row?.type !== 'INSURANCE') {
            return null;
        }

        const validToRaw = row.extractedValidTo || row.extractedData?.validTo;
        if (!validToRaw) {
            return null;
        }

        const validTo = this.toDateOnly(String(validToRaw));
        if (!validTo) {
            return 'Verzekering einddatum kon niet gelezen worden.';
        }

        const today = this.toDateOnly(new Date().toISOString());
        const millisecondsPerDay = 24 * 60 * 60 * 1000;
        const diffDays = Math.ceil((validTo.getTime() - today.getTime()) / millisecondsPerDay);

        if (diffDays < 0) {
            return `Verzekering is verlopen (${Math.abs(diffDays)} dagen geleden).`;
        }

        if (diffDays <= 30) {
            return `Verzekering vervalt binnen ${diffDays} dagen.`;
        }

        return null;
    }

    isInsuranceExpired(row: FleetDocumentRow): boolean {
        if (row?.type !== 'INSURANCE') {
            return false;
        }

        const validToRaw = row.extractedValidTo || row.extractedData?.validTo;
        if (!validToRaw) {
            return false;
        }

        const validTo = this.toDateOnly(String(validToRaw));
        const today = this.toDateOnly(new Date().toISOString());

        if (!validTo) {
            return false;
        }

        return validTo.getTime() < today.getTime();
    }

    private loadFleetDocuments(): void {
        if (!this.selectedVehicle?.uuid) {
            return;
        }

        this.loadingFleetDocuments = true;
        this.globalFilesService.getFleetVehicleDocuments(this.selectedVehicle.uuid).subscribe({
            next: (documents) => {
                this.resetFleetDocumentRows();
                const latestDocumentByType = new Map<FleetDocumentType, UploadedDocument>();
                (documents || []).forEach((doc) => {
                    const resolvedType = this.resolveFleetDocumentType(doc);
                    if (!resolvedType) {
                        return;
                    }

                    const existing = latestDocumentByType.get(resolvedType);
                    if (!existing || this.getDocumentTimestamp(doc) > this.getDocumentTimestamp(existing)) {
                        latestDocumentByType.set(resolvedType, doc);
                    }
                });

                latestDocumentByType.forEach((doc, resolvedType) => {
                    const row = this.fleetDocumentRows.find((item) => item.type === resolvedType);
                    if (!row) {
                        return;
                    }
                    row.documentId = doc.documentId;
                    row.url = doc.url;
                    row.extractedData = doc.extractedData || null;
                    row.extractionStatus = doc.extractionStatus || null;
                    row.extractedLicensePlate = this.firstNonEmpty(
                        doc.extractedLicensePlate,
                        this.getExtractedValue(row.extractedData, ['licensePlate', 'license_plate', 'plaat', 'numberPlate']),
                    );
                    row.extractedVin = this.firstNonEmpty(
                        doc.extractedVin,
                        this.getExtractedValue(row.extractedData, ['vin', 'VIN', 'chassisNumber', 'chassisNo', 'chassis', 'fieldE', 'e']),
                    );
                    row.extractedPolicyNumber = this.firstNonEmpty(
                        doc.extractedPolicyNumber,
                        this.getExtractedValue(row.extractedData, ['policyNumber', 'policy_number', 'polisnummer', 'polisNummer']),
                    );
                    row.extractedValidFrom = this.firstNonEmpty(
                        doc.extractedValidFrom,
                        this.getExtractedValue(row.extractedData, ['validFrom', 'valid_from', 'startDate']),
                    );
                    row.extractedValidTo = this.firstNonEmpty(
                        doc.extractedValidTo,
                        this.getExtractedValue(row.extractedData, ['validTo', 'valid_to', 'endDate']),
                    );
                    row.extractedFirstRegistrationDate = this.firstNonEmpty(
                        doc.extractedFirstRegistrationDate,
                        this.getExtractedValue(row.extractedData, ['firstRegistrationDate', 'first_registration_date']),
                    );
                });

                this.loadingFleetDocuments = false;
            },
            error: () => {
                this.loadingFleetDocuments = false;
            },
        });
    }

    private resetFleetDocumentRows(): void {
        this.fleetDocumentRows = this.fleetDocumentRows.map((row) => ({
            ...row,
            documentId: null,
            url: null,
            extractedData: null,
            extractionStatus: null,
            extractedLicensePlate: null,
            extractedVin: null,
            extractedPolicyNumber: null,
            extractedValidFrom: null,
            extractedValidTo: null,
            extractedFirstRegistrationDate: null,
            extracting: false,
        }));
    }

    private getDocumentTimestamp(document: UploadedDocument): number {
        const createdOn = document?.createdOn ? new Date(document.createdOn).getTime() : 0;
        const timestamp = createdOn || 0;
        return Number.isNaN(timestamp) ? 0 : timestamp;
    }

    private firstNonEmpty(...values: Array<string | null | undefined>): string | null {
        for (const value of values) {
            if (value === null || value === undefined) {
                continue;
            }
            const trimmed = String(value).trim();
            if (trimmed) {
                return trimmed;
            }
        }
        return null;
    }

    private getExtractedValue(data: Record<string, any> | null | undefined, keys: string[]): string | null {
        if (!data) {
            return null;
        }

        for (const key of keys) {
            const exact = data[key];
            if (exact !== null && exact !== undefined && String(exact).trim() !== '') {
                return String(exact).trim();
            }

            const caseInsensitiveKey = Object.keys(data).find((k) => k.toLowerCase() === key.toLowerCase());
            if (!caseInsensitiveKey) {
                continue;
            }

            const value = data[caseInsensitiveKey];
            if (value !== null && value !== undefined && String(value).trim() !== '') {
                return String(value).trim();
            }
        }

        return null;
    }

    private getDocumentLabel(documentType: FleetDocumentType): string {
        return this.fleetDocumentRows.find((row) => row.type === documentType)?.label || documentType;
    }

    private resolveFleetDocumentType(document: UploadedDocument): FleetDocumentType | null {
        if (document?.documentType && this.isFleetDocumentType(document.documentType)) {
            return document.documentType;
        }

        const description = (document?.description || '').toLowerCase();
        if (description.includes('verzeker')) {
            return 'INSURANCE';
        }
        if (description.includes('inschrijv')) {
            return 'REGISTRATION_CERTIFICATE';
        }
        if (description.includes('gelijkvorm') || description.includes('coc')) {
            return 'CONFORMITY_CERTIFICATE';
        }

        return null;
    }

    private isFleetDocumentType(value: string): value is FleetDocumentType {
        return value === 'INSURANCE' || value === 'REGISTRATION_CERTIFICATE' || value === 'CONFORMITY_CERTIFICATE';
    }

    private toDateOnly(value: string): Date | null {
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
            return null;
        }

        return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
}

type FleetDocumentType = 'INSURANCE' | 'REGISTRATION_CERTIFICATE' | 'CONFORMITY_CERTIFICATE';

interface FleetDocumentRow {
    type: FleetDocumentType;
    label: string;
    documentId?: string;
    url?: string;
    extractionStatus?: string;
    extractedData?: Record<string, any>;
    extractedLicensePlate?: string;
    extractedVin?: string;
    extractedPolicyNumber?: string;
    extractedValidFrom?: string;
    extractedValidTo?: string;
    extractedFirstRegistrationDate?: string;
    extracting?: boolean;
}
